import React from 'react';
import { motion } from 'motion/react';

interface IconProps {
  className?: string;
}

const IconFrame: React.FC<React.PropsWithChildren<IconProps>> = ({ className = 'w-8 h-8', children }) => (
  <motion.svg
    viewBox="0 0 64 64"
    className={className}
    aria-hidden="true"
    animate={{ y: [0, -2, 0] }}
    transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
  >
    {children}
  </motion.svg>
);

export const BackIcon: React.FC<IconProps> = ({ className }) => (
  <IconFrame className={className}>
    <motion.path d="M38 14 20 32l18 18" fill="none" stroke="#92400E" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" animate={{ x: [0, -3, 0] }} transition={{ duration: 1.6, repeat: Infinity }} />
    <path d="M22 32h29" fill="none" stroke="#92400E" strokeWidth="7" strokeLinecap="round" />
  </IconFrame>
);

export const AlphabetIcon: React.FC<IconProps> = ({ className }) => (
  <IconFrame className={className}>
    <motion.g animate={{ rotate: [-4, 4, -4] }} transition={{ duration: 2, repeat: Infinity }} style={{ originX: '32px', originY: '34px' }}>
      <rect x="8" y="18" width="16" height="28" rx="5" fill="#4F7CF7" /><text x="16" y="39" textAnchor="middle" fill="white" fontSize="16" fontWeight="900">A</text>
      <rect x="25" y="12" width="16" height="34" rx="5" fill="#FFB84D" /><text x="33" y="35" textAnchor="middle" fill="white" fontSize="16" fontWeight="900">B</text>
      <rect x="42" y="20" width="14" height="26" rx="5" fill="#4BC27A" /><text x="49" y="39" textAnchor="middle" fill="white" fontSize="15" fontWeight="900">C</text>
    </motion.g>
    <motion.circle cx="52" cy="10" r="3" fill="#FFD700" animate={{ scale: [0.7, 1.3, 0.7], opacity: [0.4, 1, 0.4] }} transition={{ duration: 1.4, repeat: Infinity }} />
  </IconFrame>
);

export const WritingIcon: React.FC<IconProps> = ({ className }) => (
  <IconFrame className={className}>
    <rect x="10" y="13" width="44" height="39" rx="9" fill="#FFF1D6" stroke="#F59E0B" strokeWidth="3" />
    <path d="M19 39c5-10 10 7 15-4 4-9 8 3 13-6" fill="none" stroke="#4F7CF7" strokeWidth="3" strokeLinecap="round" />
    <motion.path d="m43 16 8 8-19 19-9 2 2-9Z" fill="#FFB84D" stroke="#92400E" strokeWidth="2" animate={{ rotate: [0, 5, 0] }} transition={{ duration: 1.8, repeat: Infinity }} style={{ originX: '43px', originY: '16px' }} />
  </IconFrame>
);

export const SyllableIcon: React.FC<IconProps> = ({ className }) => (
  <IconFrame className={className}>
    <motion.g animate={{ x: [-2, 2, -2] }} transition={{ duration: 2.2, repeat: Infinity }}>
      <rect x="8" y="20" width="21" height="24" rx="7" fill="#4BC27A" /><text x="18.5" y="37" textAnchor="middle" fill="white" fontSize="14" fontWeight="900">BA</text>
      <path d="M32 32h8" stroke="#F59E0B" strokeWidth="4" strokeLinecap="round" /><path d="m37 27 6 5-6 5" fill="none" stroke="#F59E0B" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="42" y="20" width="14" height="24" rx="7" fill="#FFB84D" /><circle cx="49" cy="32" r="5" fill="#FFF7E8" />
    </motion.g>
  </IconFrame>
);

export const PuzzleIcon: React.FC<IconProps> = ({ className }) => (
  <IconFrame className={className}>
    <motion.path d="M14 20h13c-2-7 9-9 8-1 0 2-1 3-2 4h7v10c7-2 9 8 2 9-1 0-2 0-3-1v8H25v-8c-7 2-9-8-2-9 1 0 2 0 3 1V20Z" fill="#4F7CF7" stroke="#2454C6" strokeWidth="3" animate={{ rotate: [-3, 3, -3] }} transition={{ duration: 2, repeat: Infinity }} style={{ originX: '32px', originY: '32px' }} />
    <circle cx="48" cy="14" r="3" fill="#FFD700" />
  </IconFrame>
);

export const CameraIcon: React.FC<IconProps> = ({ className }) => (
  <IconFrame className={className}>
    <motion.g animate={{ rotate: [-3, 3, -3] }} transition={{ duration: 2, repeat: Infinity }} style={{ originX: '32px', originY: '34px' }}>
      <rect x="8" y="20" width="48" height="30" rx="8" fill="#FFB84D" stroke="#C97900" strokeWidth="3" /><path d="m21 20 4-7h14l4 7" fill="#FFD98A" stroke="#C97900" strokeWidth="3" />
      <circle cx="32" cy="35" r="10" fill="#EFF6FF" stroke="#4F7CF7" strokeWidth="4" /><circle cx="32" cy="35" r="4" fill="#4F7CF7" />
    </motion.g>
    <motion.path d="M48 12v8m-4-4h8" stroke="#4BC27A" strokeWidth="3" strokeLinecap="round" animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 1.5, repeat: Infinity }} />
  </IconFrame>
);

export const BookIcon: React.FC<IconProps> = ({ className }) => (
  <IconFrame className={className}>
    <motion.path d="M9 17c8-4 15-2 23 3v31c-8-5-15-7-23-3V17Zm46 0c-8-4-15-2-23 3v31c8-5 15-7 23-3V17Z" fill="#4BC27A" stroke="#18834A" strokeWidth="3" animate={{ scaleY: [1, 1.04, 1] }} transition={{ duration: 2, repeat: Infinity }} style={{ originX: '32px', originY: '32px' }} />
    <path d="M17 23h9m21 0h-9" stroke="white" strokeWidth="3" strokeLinecap="round" />
  </IconFrame>
);

export const GameIcon: React.FC<IconProps> = ({ className }) => (
  <IconFrame className={className}>
    <motion.path d="M15 25c-8-5-13 8-5 18l6 7c3 3 7 0 8-5h16c1 5 5 8 8 5l6-7c8-10 3-23-5-18-5 3-9 4-17 4s-12-1-17-4Z" fill="#EC4899" stroke="#A91B61" strokeWidth="3" animate={{ y: [0, -2, 0] }} transition={{ duration: 1.7, repeat: Infinity }} />
    <path d="M19 33v8m-4-4h8m19 0h.1m7 4h.1" stroke="white" strokeWidth="3" strokeLinecap="round" />
  </IconFrame>
);

export const SpeakerIcon: React.FC<IconProps> = ({ className }) => (
  <IconFrame className={className}>
    <path d="M11 27h10l12-9v28l-12-9H11V27Z" fill="#FFB84D" stroke="#C97900" strokeWidth="3" />
    <motion.path d="M41 25c5 4 5 10 0 14m6-20c9 8 9 18 0 26" fill="none" stroke="#4F7CF7" strokeWidth="3" strokeLinecap="round" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.4, repeat: Infinity }} />
  </IconFrame>
);

export const UploadIcon: React.FC<IconProps> = ({ className }) => (
  <IconFrame className={className}>
    <path d="M14 39v9h36v-9M32 43V15m0 0L21 26m11-11 11 11" fill="none" stroke="#64748B" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
  </IconFrame>
);

export const LockIcon: React.FC<IconProps> = ({ className }) => (
  <IconFrame className={className}>
    <rect x="14" y="27" width="36" height="27" rx="6" fill="#FFB84D" stroke="#B45309" strokeWidth="3" />
    <motion.path
      d="M22 29v-9c0-13 20-13 20 0v9"
      fill="none"
      stroke="#B45309"
      strokeWidth="5"
      strokeLinecap="round"
      animate={{ rotate: [0, 0, -34, -34, 0] }}
      transition={{ duration: 3.2, times: [0, 0.22, 0.42, 0.72, 1], repeat: Infinity, ease: 'easeInOut' }}
      style={{ originX: '22px', originY: '28px' }}
    />
    <circle cx="22" cy="29" r="3.5" fill="#B45309" />
    <circle cx="32" cy="40" r="3" fill="#FFF7E8" />
  </IconFrame>
);

export const SummaryIcon: React.FC<IconProps> = ({ className }) => (
  <IconFrame className={className}>
    <motion.rect x="10" y="16" width="44" height="36" rx="7" fill="#DBEAFE" stroke="#2563EB" strokeWidth="3" animate={{ scale: [1, 1.03, 1] }} transition={{ duration: 2, repeat: Infinity }} style={{ originX: '32px', originY: '34px' }} />
    <path d="M18 42V34m9 8V27m10 15V31m9 11V23" stroke="#2563EB" strokeWidth="5" strokeLinecap="round" />
  </IconFrame>
);

export const TipIcon: React.FC<IconProps> = ({ className }) => (
  <IconFrame className={className}>
    <motion.path d="M32 10c-11 0-18 8-18 17 0 7 4 10 8 14v6h20v-6c4-4 8-7 8-14 0-9-7-17-18-17Z" fill="#FDE68A" stroke="#D97706" strokeWidth="3" animate={{ y: [0, -2, 0] }} transition={{ duration: 2, repeat: Infinity }} />
    <path d="M25 54h14M27 59h10" stroke="#D97706" strokeWidth="3" strokeLinecap="round" />
    <motion.path d="M32 3v4M13 11l3 3m35-3-3 3" stroke="#F59E0B" strokeWidth="3" strokeLinecap="round" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.5, repeat: Infinity }} />
  </IconFrame>
);

export const SettingsIcon: React.FC<IconProps> = ({ className }) => (
  <IconFrame className={className}>
    <motion.path d="m32 9 4 5 7-1 2 7 6 3-3 6 3 6-6 3-2 7-7-1-4 5-4-5-7 1-2-7-6-3 3-6-3-6 6-3 2-7 7 1 4-5Z" fill="#CBD5E1" stroke="#475569" strokeWidth="3" animate={{ rotate: [0, 8, 0] }} transition={{ duration: 2.5, repeat: Infinity }} style={{ originX: '32px', originY: '34px' }} />
    <circle cx="32" cy="34" r="8" fill="white" stroke="#475569" strokeWidth="3" />
  </IconFrame>
);